import { useMemo, useCallback } from 'react'

import { Bookmark, Computer, Placeholder, Shield, Tag } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import type {
  DeviceCatalogView,
  FilterSection,
} from 'modules/it_management/hooks/device_catalog/types'
import {
  CATEGORY_ICONS,
  CATEGORY_IDS,
  CATEGORY_LABELS,
} from 'modules/it_management/hooks/device_catalog/constants'
import type { MarketplaceSidebarSection } from 'modules/it_management/components/device_catalog/MarketplaceSidebar'

type UseSidebarSectionsArgs = {
  activeView: DeviceCatalogView
  selectedCategory: string | null
  attributeFilters: Record<string, string[]>
  dynamicFilterOptions: FilterSection[]
  setView: (view: DeviceCatalogView) => void
  setCategory: (category: string) => void
  setAttributeFilter: (key: string, values: string[]) => void
}

// Temporary toggle: keep code path ready and flip to true when Zero-touch should be visible again.
const SHOW_ZERO_TOUCH_VIEW = false
// Temporary toggle: keep Offers menu path ready and flip to true when Offers should be visible again.
const SHOW_OFFERS_VIEW = false

export function useSidebarSections({
  activeView,
  selectedCategory,
  attributeFilters,
  dynamicFilterOptions,
  setView,
  setCategory,
  setAttributeFilter,
}: UseSidebarSectionsArgs) {
  // Filter sections come from useDeviceCatalogFilters.extractFilterOptions (delivery time, brand, attributes)
  const allFilterSections: FilterSection[] = useMemo(
    () => dynamicFilterOptions,
    [dynamicFilterOptions]
  )
  const shouldShowFilters = activeView === 'category' && selectedCategory != null

  // Check if any filters are active
  const hasActiveFilters = Object.values(attributeFilters).some((v) => v.length > 0)

  const clearAllFilters = useCallback(() => {
    Object.keys(attributeFilters).forEach((key) => {
      setAttributeFilter(key, [])
    })
  }, [attributeFilters, setAttributeFilter])

  // Active sidebar item
  const activeItemId = useMemo(() => {
    if (activeView === 'category' && selectedCategory) {
      return selectedCategory
    }
    return activeView
  }, [activeView, selectedCategory])

  // Contextual search placeholder
  const searchPlaceholder = useMemo(() => {
    if (activeView === 'category' && selectedCategory) {
      return i18n.t('it_management.device_catalog.search_in_category', {
        category: CATEGORY_LABELS[selectedCategory]?.() ?? selectedCategory,
      })
    }
    return i18n.t('it_management.device_catalog.search_placeholder')
  }, [activeView, selectedCategory])

  // Build sidebar sections
  const sections: MarketplaceSidebarSection[] = useMemo(
    () => [
      {
        type: 'navigation' as const,
        items: [
          {
            id: 'all',
            label: i18n.t('it_management.device_catalog.views.all_devices'),
            icon: Computer,
            onClick: () => setView('all'),
          },
          {
            id: 'saved',
            label: i18n.t('it_management.device_catalog.views.saved'),
            icon: Bookmark,
            onClick: () => setView('saved'),
          },
          ...(SHOW_OFFERS_VIEW
            ? [
                {
                  id: 'offers',
                  label: i18n.t('it_management.device_catalog.views.offers'),
                  icon: Tag,
                  onClick: () => setView('offers'),
                },
              ]
            : []),
          ...(SHOW_ZERO_TOUCH_VIEW
            ? [
                {
                  id: 'zero-touch',
                  label: i18n.t('it_management.device_catalog.views.zero_touch'),
                  // Keep Shield until UserProtected is exported by f0-react (f0#3802).
                  icon: Shield,
                  onClick: () => setView('zero-touch'),
                },
              ]
            : []),
        ],
      },
      {
        type: 'categories' as const,
        title: i18n.t('it_management.device_catalog.categories.label'),
        items: CATEGORY_IDS.map((catId) => ({
          id: catId,
          label: CATEGORY_LABELS[catId]?.() ?? catId,
          icon: CATEGORY_ICONS[catId] ?? Placeholder,
          onClick: () => setCategory(catId),
        })),
      },
      ...(shouldShowFilters
        ? [
            {
              type: 'filters' as const,
              sections: allFilterSections,
              onClearAll: hasActiveFilters ? clearAllFilters : undefined,
            },
          ]
        : []),
    ],
    [setView, setCategory, allFilterSections, hasActiveFilters, clearAllFilters, shouldShowFilters]
  )

  return { sections, activeItemId, searchPlaceholder }
}
